// Kişiye ait belge listesi — GET /v1/documents (spec 12, routes/documents.ts).
//
// Satır daraltması sunucudadır (document-visibility.ts): employee_self yalnız
// kendi satırlarını, manager_subtree kendisi + astlarını, executive_broad
// kiracıyı okur. Buradaki `owner_entity_id` süzgeci kapsamı yalnız DARALTIR;
// başkasının kimliğini yazan bir çalışan yine yalnız kendi satırlarını görür.
//
// Uç keyset değil offset sayfalar. Ortak `useKeysetQuery` imleci opak bir dize
// olarak taşıdığı için offset o dizeye yazılır; ikinci bir sayfalama kancası
// açmaya gerek kalmaz.

import type { DocumentCategory } from "@novemai/documents-sdk";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { type KeysetPage } from "@/api/keyset";

export interface DocumentRow {
  readonly document_id: string;
  readonly title: string;
  readonly category: string;
  readonly owner_entity_kind: string | null;
  readonly owner_entity_id: string | null;
  readonly mime_type: string | null;
  readonly bytes: number | null;
  readonly legal_hold: boolean;
  readonly lifecycle_state: string;
  readonly version: number;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * GET /v1/documents `category` values. The route does not publish the enum in
 * its OpenAPI schema, so the source is documents-sdk `DocumentCategory`. The
 * mapped type below fails typecheck when the SDK adds or drops a category; the
 * object only fixes the filter's display order.
 *
 * Type-only import: the SDK root re-exports chain.ts, which imports
 * node:crypto, and must not enter the browser bundle.
 */
const CATEGORY_ORDER: { readonly [K in DocumentCategory]: K } = {
  offer: "offer", nda: "nda", onboarding: "onboarding", benefits: "benefits",
  compensation: "compensation", tax: "tax", policy: "policy", acknowledgment: "acknowledgment",
  termination: "termination", contract: "contract", certificate: "certificate",
  performance: "performance", custom: "custom",
};
export const DOCUMENT_CATEGORIES: readonly DocumentCategory[] = Object.values(CATEGORY_ORDER);
export type { DocumentCategory };

const PAGE_SIZE = 50;

type DocumentsGet = (
  path: "/v1/documents",
  init: {
    params: {
      query: {
        owner_entity_kind: "employee";
        owner_entity_id: string;
        category?: DocumentCategory;
        limit: number;
        offset?: number;
      };
    };
  },
) => Promise<EnvelopedFetchResult<{ items: DocumentRow[] }>>;

export async function fetchEmployeeDocumentsPage(
  employeeId: string,
  cursor: string | null,
  category?: DocumentCategory,
): Promise<KeysetPage<DocumentRow>> {
  const get = platformGraph.GET as unknown as DocumentsGet;
  const offset = cursor === null ? 0 : Number(cursor);
  const data = await unwrapEnvelope(
    get("/v1/documents", {
      params: {
        query: {
          owner_entity_kind: "employee",
          owner_entity_id: employeeId,
          ...(category !== undefined && { category }),
          limit: PAGE_SIZE,
          ...(offset > 0 && { offset }),
        },
      },
    }),
  );
  // A full page may be the last one; the next read then answers empty, which is
  // cheaper than a second count query the route does not offer.
  return {
    items: data.items,
    nextCursor: data.items.length === PAGE_SIZE ? String(offset + PAGE_SIZE) : null,
  };
}
